import { Controller } from '@hotwired/stimulus';

// Connects to data-controller="gmaps-radius"
// This controller handles the radius changes and manages the radius change
// control inside the map. When the radius is changed there, it draws or redraws
// the circle we use to display the radius to people; it also dispatches an
// event that we use in, for example, the bounds controller to set appropriate
// bounds. That event is only dispatched on radius changes *after* the initial
// page load, mainly because the bounds controller handles initial panning in its
// connect() method and that'd take some refactoring to change. The event
// contains a detail property which looks like {radius, lat, lng} where
// radius is either undefined (but present) or a number (kilometers) and the lat and lng values
// are the current latitude and longitude.
export default class extends Controller {
  static targets = ['map', 'modeLink', 'controls'];

  // The radius value is in kilometers. It's '' when we are searching
  // inside the exact place borders (i. e. without a radius). (It's not a Number because life is
  // simpler if it's the thing we're submitting to the backend -- and we want that to be ''
  // sometimes so we don't have an unnecessary parameter). The
  // additionalQueryString is required because we need to know the longitude and
  // latitude, and we can't just get them from the URL -- on some landing pages,
  // they're not *in* the URL.
  static values = { radius: String, additionalQueryString: String };

  update(event) {
    this.radiusValue = event.target.value;
  }

  async radiusValueChanged(newRadius) {
    if (!this.hasMapTarget) {
      // The user didn't consent to Google maps. We're not doing anything. We're
      // also not saying we handled the initial radius change -- we mean the
      // initial radius change *after the map loads*.
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const additionalParams = new URLSearchParams(
      this.additionalQueryStringValue,
    );

    const latitudeParam =
      params.get('latitude') || additionalParams.get('latitude');

    const longitudeParam =
      params.get('longitude') || additionalParams.get('longitude');

    const locationIndependentOnly = params.get('only_location_independent');

    if (!latitudeParam || !longitudeParam || locationIndependentOnly) {
      this.initialRadiusChangeHandled = true;
      return;
    }

    const lat = parseFloat(latitudeParam);
    const lng = parseFloat(longitudeParam);

    const newValue = newRadius === '' ? undefined : parseInt(newRadius, 10);

    // Because this function might wind up getting called by code (not due to an input getting
    // clicked) we sync our inputs' state to the value.
    const input = this.controlsTarget.querySelector(
      `input[value="${newRadius}"]`,
    );
    input.checked = true;

    if (!newValue) {
      this.deleteCircle(params);
      params.delete('radius');
    } else {
      this.redrawCircle(lat, lng, newValue);
      params.set('radius', newValue);
    }

    // We do want to modify the URL (the general principle of this webpage is
    // that the URL stores a bunch of state that should be current), but we
    // don't especially want the back button to navigate back to the last radius
    // search, as that feels like worse UI. Thus, history.replaceState.
    history.replaceState(null, '', '?' + params.toString());

    this.updateModeLink();

    if (this.initialRadiusChangeHandled) {
      this.dispatch('radius-changed', {
        detail: { radius: newValue, lng, lat },
      });
    }

    this.initialRadiusChangeHandled = true;
  }

  updateModeLink() {
    const modeLinkParams = new URLSearchParams(this.modeLinkTarget.search);

    if (this.radiusValue !== '') {
      modeLinkParams.set('radius', this.radiusValue);
    } else {
      modeLinkParams.delete('radius');
    }

    this.modeLinkTarget.search = '?' + modeLinkParams.toString();
  }

  redrawCircle(lat, lng, radiusInKm) {
    if (!this.circleFillColor || !this.circleStrokeColor) {
      // We'll cache this beccause getComputedStyle is reasonably expensive.
      // These properties never change.
      const rootStyle = getComputedStyle(document.documentElement);
      const fillPropertyName = '--brand-primary-color--transparent-30';
      const strokePropertyName = '--brand-primary-color';
      this.circleFillColor = rootStyle.getPropertyValue(fillPropertyName);
      this.circleStrokeColor = rootStyle.getPropertyValue(strokePropertyName);
    }

    if (!this.circle) {
      this.circle = new google.maps.Circle({
        strokeColor: this.circleStrokeColor,
        strokeWeight: 2,
        fillColor: this.circleFillColor,
        map: this.mapTarget.innerMap,
        center: { lat, lng },
        radius: Number(radiusInKm) * 1000,
        draggable: false,
        editable: false,
      });
    } else {
      this.circle.setRadius(Number(radiusInKm) * 1000);
      this.circle.setCenter({ lat, lng });
    }
  }

  deleteCircle() {
    if (this.circle) {
      this.circle.setMap(null);
      this.circle = null;
    }
  }
}
